/**
 * useImportReviewWizard
 *
 * Fetches the review summary (single call) and manages wizard step navigation.
 * Each step component fetches its own phase data lazily via useGetReviewPhaseQuery.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEventTracking } from '@services/statsig';

import { useApproveReviewMutation, useGetReviewSummaryQuery } from '../../store/hooks';

import type { PreviewCounts, WizardStep, WizardStepId } from './ImportReviewWizard.types';

// =============================================================================
// Hook
// =============================================================================

interface UseImportReviewWizardParams {
  sessionId: string;
}

export interface UseImportReviewWizardReturn {
  // Loading state
  isLoading: boolean;
  error: boolean;

  // Summary data
  counts: PreviewCounts;
  totalRecords: number;
  actionableCount: number;

  // Wizard navigation
  steps: WizardStep[];
  activeStepIndex: number;
  activeStepId: WizardStepId;
  isFirstStep: boolean;
  isLastStep: boolean;
  previewComplete: boolean;
  slideDirection: 'left' | 'right';

  // Actions
  goNext: () => void;
  goBack: () => void;
  markPreviewComplete: () => void;
  handleApprove: (options?: {
    skipAppointments?: boolean;
    skipNewPatients?: boolean;
  }) => Promise<void>;
  dismissWizard: () => void;
  isApproving: boolean;
}

export function useImportReviewWizard({
  sessionId,
}: UseImportReviewWizardParams): UseImportReviewWizardReturn {
  const { patientImport } = useEventTracking();
  const reviewViewedRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Summary fetch — single call, powers preview + step visibility
  // ---------------------------------------------------------------------------

  const {
    data: summary,
    isLoading,
    error: fetchError,
  } = useGetReviewSummaryQuery({ sessionId });

  const counts = useMemo<PreviewCounts>(
    () => ({
      newCount: summary?.new_patients ?? 0,
      matchedCount: summary?.matched_patients ?? 0,
      appointmentCount: summary?.appointments ?? 0,
      ambiguousCount: summary?.ambiguous_patients ?? 0,
    }),
    [summary],
  );

  const totalRecords = summary?.total_records ?? 0;
  const actionableCount = counts.newCount + counts.matchedCount;

  // ---------------------------------------------------------------------------
  // Track review viewed once summary loads
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (summary && !reviewViewedRef.current) {
      reviewViewedRef.current = true;
      patientImport.reviewViewed({
        session_id: sessionId,
        total_patients: summary.total_records,
      });
    }
  }, [summary, sessionId, patientImport]);

  // ---------------------------------------------------------------------------
  // Wizard steps — only include steps that have data
  // ---------------------------------------------------------------------------

  const steps = useMemo<WizardStep[]>(() => {
    const result: WizardStep[] = [{ id: 'preview', label: 'Preview' }];
    if (counts.newCount > 0) result.push({ id: 'new-patients', label: 'New Patients' });
    if (counts.matchedCount > 0) result.push({ id: 'matched-patients', label: 'Matched Patients' });
    if (counts.appointmentCount > 0) result.push({ id: 'appointments', label: 'Appointments' });
    return result;
  }, [counts]);

  // ---------------------------------------------------------------------------
  // Navigation state
  // ---------------------------------------------------------------------------

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [previewComplete, setPreviewComplete] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');

  const activeStepId = steps[activeStepIndex]?.id ?? 'preview';
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === steps.length - 1;

  // ---------------------------------------------------------------------------
  // Track wizard step views
  // ---------------------------------------------------------------------------

  const trackedStepsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!summary || activeStepId === 'preview') return;
    if (trackedStepsRef.current.has(activeStepId)) return;
    trackedStepsRef.current.add(activeStepId);

    const itemCountMap: Record<string, number> = {
      'new-patients': counts.newCount,
      'matched-patients': counts.matchedCount,
      'appointments': counts.appointmentCount,
    };

    patientImport.wizardStepViewed({
      session_id: sessionId,
      step_id: activeStepId,
      item_count: itemCountMap[activeStepId] ?? 0,
    });
  }, [activeStepId, summary, sessionId, counts, patientImport]);

  const markPreviewComplete = useCallback(() => {
    setPreviewComplete(true);
  }, []);

  const goNext = useCallback(() => {
    if (activeStepIndex < steps.length - 1) {
      setSlideDirection('left');
      setActiveStepIndex((prev) => prev + 1);
    }
  }, [activeStepIndex, steps.length]);

  const goBack = useCallback(() => {
    if (activeStepIndex > 0) {
      setSlideDirection('right');
      setActiveStepIndex((prev) => prev - 1);
    }
  }, [activeStepIndex]);

  // ---------------------------------------------------------------------------
  // Approve / Import
  // ---------------------------------------------------------------------------

  const [approveReview, { isLoading: isApproving }] = useApproveReviewMutation();

  const handleApprove = useCallback(
    async (options?: { skipAppointments?: boolean; skipNewPatients?: boolean }) => {
      if (options?.skipAppointments) {
        patientImport.skipAppointments({
          session_id: sessionId,
          appointments_count: counts.appointmentCount,
        });
      }
      if (options?.skipNewPatients) {
        patientImport.skipNewPatients({
          session_id: sessionId,
          new_patients_count: counts.newCount,
          matched_patients_count: counts.matchedCount,
        });
      }
      patientImport.approved({
        session_id: sessionId,
        total_patients: totalRecords,
        skip_appointments: options?.skipAppointments ?? false,
        skip_new_patients: options?.skipNewPatients ?? false,
      });
      await approveReview({
        sessionId,
        skipAppointments: options?.skipAppointments,
        skipNewPatients: options?.skipNewPatients,
      });
    },
    [
      approveReview,
      sessionId,
      totalRecords,
      counts.appointmentCount,
      counts.newCount,
      counts.matchedCount,
      patientImport,
    ],
  );

  const dismissWizard = useCallback(() => {
    patientImport.wizardDismissed({
      session_id: sessionId,
      ambiguous_count: counts.ambiguousCount,
    });
    // Fire approve with 0 actionable records to mark session completed.
    // Best-effort — failure here is non-critical since the user is already navigating away.
    approveReview({ sessionId }).catch(() => {});
  }, [approveReview, sessionId, counts.ambiguousCount, patientImport]);

  return {
    isLoading,
    error: !!fetchError,

    counts,
    totalRecords,
    actionableCount,

    steps,
    activeStepIndex,
    activeStepId,
    isFirstStep,
    isLastStep,
    previewComplete,
    slideDirection,

    goNext,
    goBack,
    markPreviewComplete,
    handleApprove,
    dismissWizard,
    isApproving,
  };
}
